import React from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { deleteShopperEmail, unlinkShopperEmailMailbox } from 'apis/ShopRequestAPI';
import DownCaretIcon from 'common/Icons/DownCaretIcon';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import { getErrorMessageByPriority } from 'helpers/emails';

import { getEffectiveMailboxConnectionStatus } from './helpers';
import { ShopperEmailType } from '../types';

const ActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ActionsButton = styled(Button)`
  width: 100px;
`;

const ActionButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LinkedCount = styled.strong`
  color: var(--text-primary);
`;

type Props = {
  authenticatingEmailId: number | null;
  onEdit: (shopperEmail: ShopperEmailType) => void;
  onLinkMailbox: (shopperEmail: ShopperEmailType) => void | Promise<void>;
  shopperEmail: ShopperEmailType;
};

const ShopperEmailsTableActions = ({ authenticatingEmailId, onEdit, onLinkMailbox, shopperEmail }: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [unlinkMailboxModalOpen, setUnlinkMailboxModalOpen] = React.useState(false);
  const linkedShopRequestCount = shopperEmail.linked_shop_request_count;
  const hasLinkedShopRequests = linkedShopRequestCount > 0;

  const { mutateAsync: deleteShopperEmailMutation, isLoading: isDeleting } = useMutation({
    mutationFn: () => deleteShopperEmail(shopperEmail.id),
    onSuccess: () => {
      queryClient.invalidateQueries(['shopper-emails']);
      showMessage('success', 'Shopper email deleted successfully.');
      setDeleteModalOpen(false);
    },
  });

  const { mutateAsync: unlinkShopperEmailMailboxMutation, isLoading: isUnlinkingMailbox } = useMutation({
    mutationFn: () => unlinkShopperEmailMailbox(shopperEmail.id),
    onSuccess: () => {
      queryClient.invalidateQueries(['shopper-emails']);
      showMessage('success', 'Mailbox unlinked successfully.');
      setUnlinkMailboxModalOpen(false);
    },
  });

  const mailboxStatus = getEffectiveMailboxConnectionStatus({
    mailboxConnectionId: shopperEmail.mailbox_connection_id,
    mailboxConnectionStatus: shopperEmail.mailbox_connection_status,
  });
  const isMailboxLinked = Boolean(mailboxStatus);
  const isMailboxAuthenticated = Boolean(shopperEmail.is_mailbox_authenticated);
  const isAuthenticating = authenticatingEmailId === shopperEmail.id;
  const isBusy = isAuthenticating || isDeleting || isUnlinkingMailbox;
  const deleteModalDescription = (
    <>
      <div>Deleting this shopper contact will also delete its related shop request contact records.</div>
      <div>
        This shopper contact currently has <LinkedCount>{linkedShopRequestCount} linked</LinkedCount> shop request
        contact {linkedShopRequestCount === 1 ? 'record' : 'records'}.
      </div>
    </>
  );

  const handleDelete = React.useCallback(async () => {
    try {
      await deleteShopperEmailMutation();
    } catch (error: any) {
      showMessage(
        'error',
        getErrorMessageByPriority(error?.response?.data, 'Failed to delete shopper contact.', ['detail'])
      );
    }
  }, [deleteShopperEmailMutation, showMessage]);

  const handleUnlinkMailbox = React.useCallback(async () => {
    try {
      await unlinkShopperEmailMailboxMutation();
    } catch (error: any) {
      showMessage('error', getErrorMessageByPriority(error?.response?.data, 'Failed to unlink mailbox.', ['detail']));
    }
  }, [showMessage, unlinkShopperEmailMailboxMutation]);

  const menuItems = [
    {
      label: 'Edit',
      key: 'edit',
      disabled: isBusy || isMailboxAuthenticated,
      onClick: () => onEdit(shopperEmail),
    },
    ...(shopperEmail.email
      ? isMailboxLinked
        ? [
            {
              label: 'Unlink Mailbox',
              key: 'unlink-mailbox',
              disabled: isBusy,
              onClick: () => setUnlinkMailboxModalOpen(true),
            },
          ]
        : [
            {
              label: 'Link Mailbox',
              key: 'mailbox',
              disabled: isBusy,
              onClick: () => onLinkMailbox(shopperEmail),
            },
          ]
      : []),
    {
      label: 'Delete',
      key: 'delete',
      disabled: isBusy,
      onClick: () => setDeleteModalOpen(true),
    },
  ];

  return (
    <>
      <ActionsCell className="actions-hover-reveal">
        <Dropdown
          menu={{
            items: menuItems,
          }}
        >
          <ActionsButton disabled={isBusy}>
            <ActionButtonContent>
              <span>{isBusy ? 'Loading' : 'Edit'}</span>
              {isBusy ? <LoadingOutlined /> : <DownCaretIcon />}
            </ActionButtonContent>
          </ActionsButton>
        </Dropdown>
      </ActionsCell>
      <ConfirmModal
        open={deleteModalOpen}
        title="Delete Shopper Contact"
        description={deleteModalDescription}
        confirmLabel="Delete"
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />
      <ConfirmModal
        open={unlinkMailboxModalOpen}
        title="Unlink Mailbox"
        description="This will remove the mailbox link from this shopper contact and delete the mailbox connection if nothing else uses it."
        confirmLabel="Unlink"
        loading={isUnlinkingMailbox}
        onConfirm={handleUnlinkMailbox}
        onCancel={() => setUnlinkMailboxModalOpen(false)}
      />
    </>
  );
};

export default ShopperEmailsTableActions;
